
import React, { useEffect, useState, useCallback } from "react";
import { Modal } from "react-bootstrap";
import Axios from "axios";
import { useSelector } from "react-redux";
import { URL_LIST_ALL } from "../configs/api";
import { confirmAlert } from "react-confirm-alert";
import Spinner from "../component/Spinner";

// Dựng cây danh mục từ list phẳng (parent_id). Trả [{...dm, depth}] theo thứ tự duyệt.
export const buildDanhMucTree = (list) => {
    const byParent = {};
    list.forEach((d) => {
        const p = d.parent_id || 0;
        (byParent[p] = byParent[p] || []).push(d);
    });
    Object.keys(byParent).forEach((p) => byParent[p].sort((a, b) => (a.position || 0) - (b.position || 0) || a.id - b.id));
    const out = [];
    const walk = (parent, depth) => {
        (byParent[parent] || []).forEach((d) => {
            out.push({ ...d, depth });
            walk(d.id, depth + 1);
        });
    };
    walk(0, 0);
    return out;
};

// Modal quản lý danh mục câu hỏi (CRUD + chọn cha). onChanged() gọi sau mỗi thay đổi để parent reload.
export default function DanhMucCauHoiManager({ show, onHide, onChanged }) {
    const { user } = useSelector((state) => state.auth);
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [title, setTitle] = useState('');
    const [parentId, setParentId] = useState('');
    const [editId, setEditId] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        Axios.get(URL_LIST_ALL + "learninghub_danhmuc?status=1&limit=500&orderby=position-asc", user.config)
            .then((res) => {
                setLoading(false);
                if (res.data.status === "success") setList(res.data.data || []);
                else console.log(res.data.message);
            })
            .catch((err) => { console.log(err); setLoading(false); });
    }, [user]);

    useEffect(() => { if (show) load(); }, [show, load]);

    const tree = buildDanhMucTree(list);

    const resetForm = () => { setTitle(''); setParentId(''); setEditId(null); };

    const save = () => {
        if (!title.trim()) { alert("Vui lòng nhập tên danh mục"); return; }
        const payload = { title: title.trim(), parent_id: parentId === '' ? null : Number(parentId), status: 1 };
        const done = (res) => {
            if (res.data.status === "success") { resetForm(); load(); if (onChanged) onChanged(); }
            else confirmAlert({ title: 'Lỗi', message: res.data.message, buttons: [{ label: 'OK' }] });
        };
        if (editId) Axios.put(URL_LIST_ALL + 'learninghub_danhmuc/' + editId, payload, user.config).then(done).catch(err => console.log(err));
        else Axios.post(URL_LIST_ALL + 'learninghub_danhmuc', payload, user.config).then(done).catch(err => console.log(err));
    };

    const edit = (dm) => { setEditId(dm.id); setTitle(dm.title); setParentId(dm.parent_id || ''); };

    const remove = (dm) => {
        const hasChild = list.some((d) => d.parent_id === dm.id);
        confirmAlert({
            title: 'Xóa danh mục',
            message: hasChild
                ? 'Danh mục này có danh mục con. Xóa sẽ ảnh hưởng các danh mục con. Tiếp tục?'
                : 'Bạn chắc chắn muốn xóa danh mục "' + dm.title + '"?',
            buttons: [
                {
                    label: 'Xóa', onClick: () => {
                        Axios.delete(URL_LIST_ALL + 'learninghub_danhmuc/' + dm.id, user.config)
                            .then((res) => { if (res.data.status === "success") { if (editId === dm.id) resetForm(); load(); if (onChanged) onChanged(); } })
                            .catch(err => console.log(err));
                    }
                },
                { label: 'Hủy' },
            ],
        });
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>Quản lý danh mục câu hỏi</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="row mb-3">
                    <div className="col-12 col-sm-5 form-group">
                        <label>Tên danh mục</label>
                        <input type="text" className="form-control height32" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="VD: Toán, An toàn lao động..." />
                    </div>
                    <div className="col-12 col-sm-5 form-group">
                        <label>Danh mục cha</label>
                        <select className="form-select height32" value={parentId} onChange={(e) => setParentId(e.target.value)}>
                            <option value="">-- Không (danh mục gốc) --</option>
                            {tree.filter((d) => d.id !== editId).map((d) => (
                                <option key={d.id} value={d.id}>{' '.repeat(d.depth * 3)}{d.depth > 0 ? '└ ' : ''}{d.title}</option>
                            ))}
                        </select>
                    </div>
                    <div className="col-12 col-sm-2 form-group d-flex align-items-end">
                        <button type="button" className="btn btn-success w-100" onClick={save}>{editId ? 'Cập nhật' : 'Thêm'}</button>
                    </div>
                    {editId &&
                        <div className="col-12"><button type="button" className="btn btn-sm btn-link" onClick={resetForm}>Hủy sửa</button></div>}
                </div>

                {loading ? <div className="w-100 text-center py-2"><Spinner /></div> :
                    <ul className="w-100 list-data" style={{ listStyle: 'none', padding: 0, maxHeight: 320, overflowY: 'auto' }}>
                        {tree.length === 0 && <li className="text-muted">Chưa có danh mục nào.</li>}
                        {tree.map((d) => (
                            <li key={d.id} className="d-flex align-items-center border-bottom py-2">
                                <span style={{ paddingLeft: d.depth * 18 }}>{d.depth > 0 ? '└ ' : ''}{d.title}</span>
                                <span className="ml-auto">
                                    <button type="button" className="btn btn-sm btn-warning mr-1" onClick={() => edit(d)}><span className="fas fa-pencil-alt"></span></button>
                                    <button type="button" className="btn btn-sm btn-danger" onClick={() => remove(d)}><span className="fas fa-trash"></span></button>
                                </span>
                            </li>
                        ))}
                    </ul>}
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-secondary" onClick={onHide}>Đóng</button>
            </Modal.Footer>
        </Modal>
    );
}
